import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Radio, ShieldCheck } from "lucide-react";

const Row = ({ ok, label, hint, testid }) => (
  <div className="flex items-center justify-between py-2.5 text-sm" data-testid={testid}>
    <span className="flex items-center gap-2">
      {ok ? <CheckCircle2 className="w-4 h-4 text-[hsl(140_60%_40%)]" /> : <XCircle className="w-4 h-4 text-muted-foreground" />}
      {label}
    </span>
    <span className="text-muted-foreground font-mono text-xs">{hint || (ok ? "Set" : "Missing")}</span>
  </div>
);

export default function SmsConnectionCard({ overview, onChange }) {
  const [confirm, setConfirm] = useState(false);
  const live = overview.sms_live;

  const setLive = async (value) => {
    try {
      await api.put("/settings/sms", { sms_enabled: overview.sms_enabled, sms_live: value });
      toast.success(value ? "Live SMS enabled — reminders will now be sent" : "Live SMS turned off (test mode)");
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setConfirm(false);
    }
  };

  return (
    <section className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm" data-testid="sms-connection-card">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="label-caps text-muted-foreground mb-2">Twilio connection</div>
          <div className={`inline-flex items-center gap-2 rounded-sm px-3 py-1.5 text-sm border ${live ? "border-[hsl(140_60%_45%)]/50 bg-[hsl(140_60%_45%)]/10" : "border-brand/50 bg-brand/10"}`} data-testid="sms-live-status">
            <Radio className={`w-4 h-4 ${live ? "text-[hsl(140_60%_40%)] animate-pulse" : "text-brand"}`} />
            {live ? "LIVE — real SMS are being sent" : "TEST MODE — nothing is sent to clients"}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">Live sending</span>
          <Switch
            checked={live}
            disabled={!overview.configured && !live}
            onCheckedChange={(v) => (v ? setConfirm(true) : setLive(false))}
            data-testid="sms-live-switch"
          />
        </div>
      </div>

      <div className="mt-6 divide-y divide-border/60">
        <Row ok={overview.account_sid_set} label="TWILIO_ACCOUNT_SID" hint={overview.account_sid_hint} testid="cred-account-sid" />
        <Row ok={overview.api_key_set} label="TWILIO_API_KEY" hint={overview.api_key_hint} testid="cred-api-key" />
        <Row ok={overview.api_secret_set} label="TWILIO_API_SECRET" hint={overview.api_secret_set ? "Set (hidden)" : ""} testid="cred-api-secret" />
        <Row ok={overview.messaging_service_sid_set} label="TWILIO_MESSAGING_SERVICE_SID" hint={overview.messaging_service_sid_hint} testid="cred-messaging-service" />
        <Row ok={!!overview.business_contact_number} label="BUSINESS_CONTACT_NUMBER" hint={overview.business_contact_number || (overview.business_phone ? `Missing · using ${overview.business_phone}` : "")} testid="cred-business-contact" />
        <Row ok={overview.auth_token_set} label="Auth Token (verifies Twilio callbacks only)" hint={overview.auth_token_set ? "Set (hidden)" : ""} testid="cred-auth-token" />
      </div>
      {!overview.configured && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="sms-creds-missing">
          Enter the missing values in the backend environment (Deployments → Environment variables after publishing). Live sending stays locked until all four Twilio values are set.
        </p>
      )}

      <div className="mt-6 grid sm:grid-cols-2 gap-3 text-sm">
        <div className="rounded-sm border border-border/60 px-4 py-3" data-testid="sms-sender-check">
          <div className="label-caps text-muted-foreground mb-1">Sender ID</div>
          {overview.last_sender ? (
            <span className={overview.last_sender === overview.expected_sender ? "text-[hsl(140_60%_32%)] font-medium" : "text-destructive font-medium"}>
              {overview.last_sender === overview.expected_sender ? `Confirmed: ${overview.last_sender}` : `Twilio reported "${overview.last_sender}" — expected ${overview.expected_sender}`}
            </span>
          ) : <span className="text-muted-foreground">Expected {overview.expected_sender} · confirmed after the first live delivery report</span>}
        </div>
        <div className="rounded-sm border border-border/60 px-4 py-3" data-testid="sms-scheduler-status">
          <div className="label-caps text-muted-foreground mb-1">Reminder scheduler</div>
          <span>Every minute + 15-min backup{overview.cron_secret_set ? "" : " (backup not configured)"}</span>
          {overview.reminders_last_run && <div className="text-xs text-muted-foreground mt-0.5">Last check {new Date(overview.reminders_last_run.at).toLocaleString("en-AU", { timeZone: "Australia/Brisbane" })} · {overview.reminders_last_run.source}</div>}
        </div>
      </div>

      <div className="mt-6 text-xs text-muted-foreground space-y-1.5" data-testid="sms-webhook-urls">
        <div className="flex items-center gap-2 font-medium text-foreground"><ShieldCheck className="w-4 h-4 text-brand" /> Signed Twilio webhooks</div>
        <div>Delivery status: <code className="break-all">{overview.callback_url || "Not set"}</code></div>
        <div>Incoming replies (STOP/START): <code className="break-all">{overview.inbound_url || "Not set"}</code></div>
      </div>

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="sms-live-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Start sending real SMS?</AlertDialogTitle>
            <AlertDialogDescription>
              Only turn this on once your Sender ID "TSTouch" is approved by Twilio. Test SMS, change alerts and
              (if Automatic reminders is also on) 24-hour reminders will reach real phones, and your Twilio account will be charged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="sms-live-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => setLive(true)} data-testid="sms-live-confirm">Yes, go live</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
